import React, { useMemo, useState, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';

interface LynaAvatarProps {
    size?: number | string;
    isCelebrating?: boolean;
    isHovered?: boolean;
    isMuted?: boolean;
    isWalking?: boolean;
    isHappy?: boolean;
    isThinking?: boolean;
    animationEnabled?: boolean;
    sx?: any;
}

export const LynaAvatar: React.FC<LynaAvatarProps> = ({ 
    size = 100, 
    isCelebrating = false, 
    isHovered = false, 
    isMuted = false, 
    isWalking = false,
    isHappy = false,
    isThinking = false,
    animationEnabled = true,
    sx = {}
}) => {
    const theme = useTheme();

    // Estado interno para comportamientos idle aleatorios
    const [idleBehavior, setIdleBehavior] = useState<'purr' | 'stretch' | 'play' | 'yawn' | 'groom'>('purr');

    // Cambiar comportamiento idle cada 8-15 segundos
    useEffect(() => {
        if (!animationEnabled || isCelebrating || isWalking || isThinking) return;
        
        const behaviors: ('purr' | 'stretch' | 'play' | 'yawn' | 'groom')[] = ['purr', 'stretch', 'play', 'yawn', 'groom'];
        const randomInterval = () => Math.floor(Math.random() * 7000) + 8000;
        
        let timeout: NodeJS.Timeout;
        const cycle = () => {
            setIdleBehavior(behaviors[Math.floor(Math.random() * behaviors.length)]);
            timeout = setTimeout(cycle, randomInterval());
        };
        cycle();
        return () => clearTimeout(timeout);
    }, [animationEnabled, isCelebrating, isWalking, isThinking]);

    // Colores base de Lyna
    const furWhite = '#FFFFFF';
    const furBrown = '#D3A681'; 
    const furBlack = '#2D3436';
    const earInner = '#FFD1E8';
    const eyeColor = isMuted ? '#999' : '#1A1A1A';
    const blushColor = 'rgba(255, 182, 193, 0.6)';
    const padColor = '#FFD1E8';

    // Variantes de baile para celebrar
    const danceVariant = useMemo(() => {
        const dances = ['kawaiiDanceLyna', 'jumpDanceLyna', 'wiggleDanceLyna', 'spinDanceLyna'];
        return dances[Math.floor(Math.random() * dances.length)];
    }, [isCelebrating]);

    // Variantes de caminata
    const walkVariant = useMemo(() => {
        const walks = ['cuteWalkLyna', 'bouncyWalkLyna'];
        return walks[Math.floor(Math.random() * walks.length)];
    }, [isWalking]);

    const getAnimation = () => {
        if (!animationEnabled) return 'none';
        if (isCelebrating) return `${danceVariant} 0.4s ease-in-out infinite alternate`;
        if (isWalking) return `${walkVariant} 0.5s ease-in-out infinite`;
        if (isThinking) return 'thinkingTiltLyna 2.5s ease-in-out infinite';
        
        // Idle con animación base + comportamiento específico
        const baseIdle = 'idleFloatLyna 4s ease-in-out infinite, idleBreatheLyna 3s ease-in-out infinite';
        const behaviorAnim = getIdleBehaviorAnimation();
        return behaviorAnim ? `${baseIdle}, ${behaviorAnim}` : baseIdle;
    };

    const getIdleBehaviorAnimation = () => {
        if (idleBehavior === 'purr') return 'purrVibrateLyna 0.15s ease-in-out infinite';
        if (idleBehavior === 'stretch') return 'idleStretchLyna 4s ease-in-out infinite';
        if (idleBehavior === 'play') return 'playWithTailLyna 3s ease-in-out infinite';
        if (idleBehavior === 'yawn') return 'idleYawnLyna 5s ease-in-out infinite';
        if (idleBehavior === 'groom') return 'groomPawLyna 3.5s ease-in-out infinite';
        return null;
    };

    // Determinar si mostrar elementos específicos del comportamiento idle
    const showYawn = animationEnabled && idleBehavior === 'yawn' && !isCelebrating && !isWalking && !isThinking;
    const showGroom = animationEnabled && idleBehavior === 'groom' && !isCelebrating && !isWalking && !isThinking;
    const showPlay = animationEnabled && idleBehavior === 'play' && !isCelebrating && !isWalking && !isThinking;

    return (
        <Box
            sx={{
                width: size,
                height: size,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: getAnimation(),
                // ✨ KEYFRAMES COMPLETOS
                '@keyframes idleFloatLyna': {
                    '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                    '25%': { transform: 'translateY(-3%) rotate(1deg)' },
                    '50%': { transform: 'translateY(-5%) rotate(0deg)' },
                    '75%': { transform: 'translateY(-3%) rotate(-1deg)' }
                },
                '@keyframes idleBreatheLyna': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.02)' }
                },
                '@keyframes idleEarTwitchLyna': {
                    '0%, 90%, 100%': { transform: 'rotate(0deg)' },
                    '92%': { transform: 'rotate(-8deg)' },
                    '95%': { transform: 'rotate(8deg)' },
                    '98%': { transform: 'rotate(-4deg)' }
                },
                '@keyframes idleTailWagLyna': {
                    '0%, 100%': { transform: 'rotate(0deg)' },
                    '25%': { transform: 'rotate(8deg)' },
                    '75%': { transform: 'rotate(-8deg)' }
                },
                '@keyframes idleEyeShiftLyna': {
                    '0%, 45%, 55%, 100%': { transform: 'translateX(0)' },
                    '50%': { transform: 'translateX(2px)' }
                },
                // 🐱 Comportamientos Idle
                '@keyframes purrVibrateLyna': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(0.5px)' }
                },
                '@keyframes idleStretchLyna': {
                    '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
                    '20%': { transform: 'scale(1.05, 0.95) rotate(-2deg)' },
                    '40%': { transform: 'scale(0.95, 1.1) rotate(3deg)' },
                    '60%': { transform: 'scale(1.08, 0.92) rotate(-1deg)' },
                    '80%': { transform: 'scale(1) rotate(1deg)' }
                },
                '@keyframes playWithTailLyna': {
                    '0%, 100%': { transform: 'rotate(0deg)' },
                    '25%': { transform: 'rotate(-15deg) translateY(-2px)' },
                    '50%': { transform: 'rotate(0deg) translateY(-4px)' },
                    '75%': { transform: 'rotate(15deg) translateY(-2px)' }
                },
                '@keyframes idleYawnLyna': {
                    '0%, 10%, 90%, 100%': { transform: 'scaleY(1)' },
                    '30%': { transform: 'scaleY(0.95)' },
                    '50%': { transform: 'scaleY(0.85) translateY(-2%)' },
                    '70%': { transform: 'scaleY(0.95)' }
                },
                '@keyframes groomPawLyna': {
                    '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                    '25%': { transform: 'translateY(-5px) rotate(-20deg)' },
                    '50%': { transform: 'translateY(-8px) rotate(-35deg)' },
                    '75%': { transform: 'translateY(-5px) rotate(-20deg)' }
                },
                // 🎉 Celebrating
                '@keyframes thinkingTiltLyna': {
                    '0%, 100%': { transform: 'rotate(-5deg) translateY(0)' },
                    '50%': { transform: 'rotate(8deg) translateY(-3%)' }
                },
                '@keyframes kawaiiDanceLyna': {
                    '0%': { transform: 'scale(1.05) translateY(0) rotate(-8deg)' },
                    '100%': { transform: 'scale(1.1) translateY(-12%) rotate(12deg)' }
                },
                '@keyframes jumpDanceLyna': {
                    '0%': { transform: 'translateY(0) scaleX(1) scaleY(1)' },
                    '50%': { transform: 'translateY(-18%) scaleX(0.92) scaleY(1.08)' },
                    '100%': { transform: 'translateY(0) scaleX(1.05) scaleY(0.95)' }
                },
                '@keyframes wiggleDanceLyna': {
                    '0%': { transform: 'translateX(-6%) rotate(-6deg) scaleY(1)' },
                    '50%': { transform: 'translateX(0) rotate(0deg) scaleY(1.05)' },
                    '100%': { transform: 'translateX(6%) rotate(6deg) scaleY(1)' }
                },
                '@keyframes spinDanceLyna': {
                    '0%': { transform: 'rotate(0deg) scale(1)' },
                    '50%': { transform: 'rotate(180deg) scale(1.1)' },
                    '100%': { transform: 'rotate(360deg) scale(1)' }
                },
                '@keyframes celebrateSparkleLyna': {
                    '0%': { opacity: 0, transform: 'scale(0) rotate(0deg)' },
                    '50%': { opacity: 1, transform: 'scale(1.2) rotate(180deg)' },
                    '100%': { opacity: 0, transform: 'scale(0) rotate(360deg)' }
                },
                // 🚶 Walking
                '@keyframes cuteWalkLyna': {
                    '0%': { transform: 'translateY(0) rotate(-2deg)' },
                    '25%': { transform: 'translateY(-4%) rotate(1deg)' },
                    '50%': { transform: 'translateY(0) rotate(2deg)' },
                    '75%': { transform: 'translateY(-4%) rotate(-1deg)' },
                    '100%': { transform: 'translateY(0) rotate(-2deg)' }
                },
                '@keyframes bouncyWalkLyna': {
                    '0%': { transform: 'translateY(0) scaleX(1)' },
                    '25%': { transform: 'translateY(-5%) scaleX(0.98)' },
                    '50%': { transform: 'translateY(0) scaleX(1.02)' },
                    '75%': { transform: 'translateY(-5%) scaleX(0.98)' },
                    '100%': { transform: 'translateY(0) scaleX(1)' }
                },
                '@keyframes walkLegLiftL': {
                    '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                    '25%': { transform: 'translateY(-8px) rotate(-10deg)' },
                    '75%': { transform: 'translateY(-4px) rotate(5deg)' }
                },
                '@keyframes walkLegLiftR': {
                    '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                    '25%': { transform: 'translateY(-4px) rotate(5deg)' },
                    '75%': { transform: 'translateY(-8px) rotate(-10deg)' }
                },
                '@keyframes walkTailSyncLyna': {
                    '0%, 100%': { transform: 'rotate(0deg)' },
                    '25%': { transform: 'rotate(15deg)' },
                    '75%': { transform: 'rotate(-15deg)' }
                },
                // ✨ Detalles
                '@keyframes kawaiiBlinkAvatar': { 
                    '0%, 90%, 100%': { transform: 'scaleY(1)' }, 
                    '95%': { transform: 'scaleY(0.1)' } 
                },
                '@keyframes wiggleEarsAvatar': { 
                    '0%, 100%': { transform: 'rotate(0deg)' }, 
                    '25%': { transform: 'rotate(-6deg)' }, 
                    '75%': { transform: 'rotate(6deg)' } 
                },
                '@keyframes softWagAvatar': { 
                    '0%, 100%': { transform: 'rotate(0deg)' }, 
                    '50%': { transform: 'rotate(12deg)' } 
                },
                '@keyframes wagTailFast': { 
                    '0%': { transform: 'rotate(-25deg)' }, 
                    '100%': { transform: 'rotate(35deg)' } 
                },
                '@keyframes blushPulseAvatar': { 
                    '0%, 100%': { opacity: 0.5, transform: 'scale(1)' }, 
                    '50%': { opacity: 0.85, transform: 'scale(1.15)' } 
                },
                '@keyframes bounceHatAvatar': {
                    from: { transform: 'translateX(-50%) translateY(0) rotate(-5deg)' },
                    to: { transform: 'translateX(-50%) translateY(-8px) rotate(5deg)' }
                },
                '@keyframes purrWaveLyna': {
                    '0%, 100%': { opacity: 0, transform: 'translateY(0) scale(0.8)' },
                    '50%': { opacity: 0.7, transform: 'translateY(-10px) scale(1.2)' }
                },
                ...sx
            }}
        >
            <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '90%', height: '90%' }}>
                <defs>
                    <filter id="softShadowLynaAvatar" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="2.5" />
                        <feOffset dx="0" dy="2" result="offsetblur" />
                        <feComponentTransfer><feFuncA type="linear" slope="0.4" /></feComponentTransfer>
                        <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <clipPath id="headClipAvatar"><ellipse cx="50" cy="45" rx="32" ry="28" /></clipPath>
                    <clipPath id="bodyClipAvatar"><circle cx="50" cy="70" r="28" /></clipPath>
                </defs>

                {/* ✨ COLA - Animación contextual + más visible */}
                <g style={{ 
                    transformOrigin: '70px 80px', 
                    animation: animationEnabled 
                        ? (isCelebrating || isHappy) 
                            ? 'wagTailFast 0.25s ease-in-out infinite alternate' 
                            : isWalking 
                                ? 'walkTailSyncLyna 0.5s ease-in-out infinite' 
                                : isHovered 
                                    ? 'wiggleEarsAvatar 0.3s ease-in-out infinite' 
                                    : showPlay
                                        ? 'playWithTailLyna 3s ease-in-out infinite'
                                        : 'softWagAvatar 4s ease-in-out infinite, idleTailWagLyna 6s ease-in-out infinite'
                        : 'none'
                }}>
                    <path d="M70 80C85 80 95 70 95 55C95 40 85 35 78 40" stroke={furWhite} strokeWidth="11" strokeLinecap="round" />
                    <path d="M85 75C88 70 95 65 92 60" stroke={furBrown} strokeWidth="11" strokeLinecap="round" />
                    <path d="M92 55C95 50 92 45 88 42" stroke={furBlack} strokeWidth="11" strokeLinecap="round" />
                </g>

                {/* 🐾 PATAS TRASERAS - ¡AHORA MÁS VISIBLES! (renderizadas ANTES del cuerpo y más grandes) */}
                <g style={{ 
                    animation: animationEnabled && isWalking ? 'walkLegLiftL 0.5s ease-in-out infinite' : 'none',
                    transformOrigin: '32px 90px'
                }}>
                    <circle cx="32" cy="90" r="7.5" fill={furWhite} stroke="#eee" strokeWidth="0.5" />
                    <circle cx="32" cy="90" r="4" fill={padColor} opacity="0.7" />
                </g>
                <g style={{ 
                    animation: animationEnabled && isWalking ? 'walkLegLiftR 0.5s ease-in-out infinite 0.25s' : 'none',
                    transformOrigin: '68px 90px'
                }}>
                    <circle cx="68" cy="90" r="7.5" fill={furWhite} stroke="#eee" strokeWidth="0.5" />
                    <circle cx="68" cy="90" r="4" fill={padColor} opacity="0.7" />
                </g>

                {/* 👂 OREJAS - ¡AHORA MÁS VISIBLES! (renderizadas ANTES de la cabeza y ligeramente más grandes) */}
                <g style={{ 
                    transformOrigin: '50px 45px', 
                    animation: animationEnabled 
                        ? (isHovered || isHappy || isCelebrating) 
                            ? 'wiggleEarsAvatar 0.35s ease-in-out infinite' 
                            : showPlay || showGroom
                                ? 'idleEarTwitchLyna 3s ease-in-out infinite'
                                : 'idleEarTwitchLyna 8s ease-in-out infinite'
                        : 'none' 
                }}>
                    {/* Oreja izquierda */}
                    <path d="M23 38C17 22 33 14 42 32" fill={furWhite} stroke="rgba(0,0,0,0.1)" strokeWidth="1" />
                    <path d="M25 36C21 26 29 20 36 28" fill={furBrown} />
                    <path d="M27 33C24 26 32 23 36 31" fill={earInner} opacity="0.9" />
                    {/* Oreja derecha */}
                    <path d="M77 38C83 22 67 14 58 32" fill={furBlack} stroke="rgba(0,0,0,0.1)" strokeWidth="1" />
                    <path d="M73 33C76 26 68 23 64 31" fill={earInner} opacity="0.9" />
                </g>

                {/* 💫 CUERPO - Respiración + vibración de ronroneo */}
                <g filter="url(#softShadowLynaAvatar)" style={{
                    transformOrigin: '50px 70px',
                    animation: animationEnabled && !isCelebrating && !isWalking && !isThinking
                        ? `idleBreatheLyna 3s ease-in-out infinite${idleBehavior === 'purr' ? ', purrVibrateLyna 0.15s ease-in-out infinite' : ''}`
                        : 'none'
                }}>
                    <circle cx="50" cy="70" r="28" fill={furWhite} />
                    <g clipPath="url(#bodyClipAvatar)">
                        <path d="M22 60C25 50 45 55 50 65C55 75 35 85 30 80" fill={furBrown} opacity="0.9" />
                        <path d="M60 65C65 55 80 60 75 75C70 85 55 80 60 70" fill={furBlack} opacity="0.9" />
                    </g>
                    {/* Ondas de ronroneo visibles */}
                    {idleBehavior === 'purr' && animationEnabled && (
                        <>
                            <circle cx="50" cy="70" r="32" fill="none" stroke={blushColor} strokeWidth="1" opacity="0.4" style={{ animation: 'purrWaveLyna 1.5s ease-out infinite' }} />
                            <circle cx="50" cy="70" r="36" fill="none" stroke={blushColor} strokeWidth="0.5" opacity="0.2" style={{ animation: 'purrWaveLyna 1.5s ease-out infinite 0.3s' }} />
                        </>
                    )}
                </g>

                {/* 🐾 PATAS DELANTERAS - Con animación de grooming si corresponde */}
                <g style={{ 
                    animation: animationEnabled && isWalking 
                        ? 'walkLegLiftR 0.5s ease-in-out infinite 0.25s' 
                        : showGroom 
                            ? 'groomPawLyna 3.5s ease-in-out infinite' 
                            : 'none',
                    transformOrigin: '38px 82px'
                }}>
                    <ellipse cx="38" cy="82" rx="7.5" ry="9" fill={furWhite} stroke="#eee" strokeWidth="0.5" />
                    <circle cx="38" cy="82" r="3.5" fill={padColor} opacity="0.85" />
                </g>
                <g style={{ 
                    animation: animationEnabled && isWalking ? 'walkLegLiftL 0.5s ease-in-out infinite' : 'none',
                    transformOrigin: '62px 82px'
                }}>
                    <ellipse cx="62" cy="82" rx="7.5" ry="9" fill={furWhite} stroke="#eee" strokeWidth="0.5" />
                    <circle cx="62" cy="82" r="3.5" fill={padColor} opacity="0.85" />
                </g>

                {/* 🧠 CABEZA - Float + micro-movimientos */}
                <g filter="url(#softShadowLynaAvatar)" style={{
                    transformOrigin: '50px 45px',
                    animation: animationEnabled && !isCelebrating && !isWalking && !isThinking
                        ? 'idleFloatLyna 4s ease-in-out infinite'
                        : 'none'
                }}>
                    <ellipse cx="50" cy="45" rx="32" ry="28" fill={furWhite} />
                    <g clipPath="url(#headClipAvatar)">
                        <path d="M60 25C70 25 85 35 80 50C75 60 65 55 60 45Z" fill={furBrown} />
                        <path d="M15 35C25 30 40 35 38 48C35 55 25 55 18 45Z" fill={furBlack} />
                    </g>
                </g>

                {/* 😊 MEJILLAS */}
                <circle cx="30" cy="52" r="5" fill={blushColor} style={{ 
                    animation: animationEnabled ? 'blushPulseAvatar 2.5s ease-in-out infinite' : 'none' 
                }} />
                <circle cx="70" cy="52" r="5" fill={blushColor} style={{ 
                    animation: animationEnabled ? 'blushPulseAvatar 2.5s ease-in-out infinite 1.25s' : 'none' 
                }} />

                {/* 👀 OJOS - Con estado de bostezo si corresponde */}
                {isMuted ? (
                    <g stroke={eyeColor} strokeWidth="3" strokeLinecap="round">
                        <path d="M35 48Q40 43 45 48" /><path d="M55 48Q60 43 65 48" />
                    </g>
                ) : isHappy || isCelebrating ? (
                    <g stroke={eyeColor} strokeWidth="3" strokeLinecap="round" fill="none">
                        <path d="M33 50 Q40 42 47 50" /><path d="M53 50 Q60 42 67 50" />
                    </g>
                ) : showYawn ? (
                    // Ojos cerrados en bostezo
                    <g stroke={eyeColor} strokeWidth="3.5" strokeLinecap="round" fill="none">
                        <path d="M34 49Q40 53 46 49" /><path d="M54 49Q60 53 66 49" />
                    </g>
                ) : (
                    <g>
                        <g style={{ 
                            transformOrigin: '40px 48px', 
                            animation: animationEnabled ? 'kawaiiBlinkAvatar 6s ease-in-out infinite, idleEyeShiftLyna 5s ease-in-out infinite' : 'none' 
                        }}>
                            <circle cx="40" cy="48" r="5.5" fill={eyeColor} />
                            <circle cx="38.5" cy="45.5" r="2" fill="white" />
                        </g>
                        <g style={{ 
                            transformOrigin: '60px 48px', 
                            animation: animationEnabled ? 'kawaiiBlinkAvatar 6s ease-in-out infinite 0.1s, idleEyeShiftLyna 5s ease-in-out infinite 0.05s' : 'none' 
                        }}>
                            <circle cx="60" cy="48" r="5.5" fill={eyeColor} />
                            <circle cx="58.5" cy="45.5" r="2" fill="white" />
                        </g>
                    </g>
                )}

                {/* 👃 Nariz y Boca - Con animación de bostezo */}
                <path 
                    d={showYawn ? "M47 54Q50 60 53 54" : "M48 54Q50 56.5 52 54"} 
                    stroke="#FF5252" 
                    strokeWidth={showYawn ? 3 : 2.5} 
                    strokeLinecap="round" 
                    fill={showYawn ? "#FFCDD2" : "#FFCDD2"} 
                />
                <path
                    d={isCelebrating || isHappy ? "M42 58Q50 66 58 58" : showYawn ? "M40 60Q50 72 60 60" : "M44 58.5Q47 61.5 50 58.5Q53 61.5 56 58.5"}
                    stroke={eyeColor}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    fill={isCelebrating || isHappy || showYawn ? "#FF5252" : "none"}
                    opacity="0.9"
                />

                {/* 💬 Burbuja de ronroneo */}
                {idleBehavior === 'purr' && animationEnabled && !isCelebrating && !isWalking && !isThinking && (
                    <g style={{ animation: 'purrWaveLyna 2s ease-in-out infinite' }}>
                        <text x="75" y="40" fontSize="10" fill={blushColor} fontWeight="bold">~</text>
                        <text x="82" y="35" fontSize="8" fill={blushColor} opacity="0.7">♪</text>
                    </g>
                )}

                {/* 🐾 Patita levantada para grooming */}
                {showGroom && (
                    <g style={{ 
                        transformOrigin: '38px 82px',
                        animation: 'groomPawLyna 3.5s ease-in-out infinite'
                    }}>
                        <ellipse cx="38" cy="72" rx="5" ry="6" fill={furWhite} stroke="#eee" strokeWidth="0.5" />
                        <circle cx="38" cy="72" r="2.5" fill={padColor} opacity="0.8" />
                    </g>
                )}

                {/* ✨ EFECTOS EXTRA PARA CELEBRAR */}
                {isCelebrating && animationEnabled && (
                    <>
                        <Box sx={{
                            position: 'absolute',
                            top: '10%',
                            left: '15%',
                            fontSize: '14px',
                            animation: 'celebrateSparkleLyna 1s ease-in-out infinite',
                            opacity: 0.9
                        }}>✨</Box>
                        <Box sx={{
                            position: 'absolute',
                            top: '20%',
                            right: '10%',
                            fontSize: '12px',
                            animation: 'celebrateSparkleLyna 1.2s ease-in-out infinite 0.3s',
                            opacity: 0.9
                        }}>⭐</Box>
                        <Box sx={{
                            position: 'absolute',
                            bottom: '15%',
                            left: '20%',
                            fontSize: '13px',
                            animation: 'celebrateSparkleLyna 0.9s ease-in-out infinite 0.6s',
                            opacity: 0.9
                        }}>💖</Box>
                    </>
                )}

                {/* 🎵 Notas musicales al celebrar */}
                {isCelebrating && animationEnabled && (
                    <>
                        <Box sx={{
                            position: 'absolute',
                            top: '-10px',
                            left: '30%',
                            fontSize: '16px',
                            animation: 'bounceHatAvatar 0.6s ease-in-out infinite',
                            opacity: 0.95
                        }}>🎵</Box>
                        <Box sx={{
                            position: 'absolute',
                            top: '-5px',
                            right: '25%',
                            fontSize: '14px',
                            animation: 'bounceHatAvatar 0.7s ease-in-out infinite 0.2s',
                            opacity: 0.95
                        }}>🎶</Box>
                    </>
                )}

                <style>
                    {`
                    @keyframes wagTailFast { 0% { transform: rotate(-25deg); } 100% { transform: rotate(35deg); } }
                    @keyframes kawaiiBlinkAvatar { 0%, 90%, 100% { transform: scaleY(1); } 95% { transform: scaleY(0.1); } }
                    @keyframes blushPulseAvatar { 0%, 100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 0.85; transform: scale(1.15); } }
                    `}
                </style>
            </svg>

            {/* 💭 Icono de pensamiento */}
            {isThinking && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: -5, 
                    right: 5, 
                    fontSize: '18px', 
                    animation: 'idleFloatLyna 2.5s ease-in-out infinite',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}>💭</Box>
            )}

            {/* 👑 Corona al celebrar */}
            {isCelebrating && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '28px',
                        animation: 'bounceHatAvatar 0.5s ease-in-out infinite alternate',
                        filter: 'drop-shadow(0 3px 6px rgba(255,215,0,0.4))',
                        zIndex: 2
                    }}
                >
                    👑
                </Box>
            )}
        </Box>
    );
};

export default LynaAvatar;